import { useState } from 'react';
import { useFormik } from 'formik';
import { useToast } from '@withjoy/joykit';
import { useCreateContactsWithoutTagsGuestListMutation, GetEventContactsDocument } from '@graphql/generated';
import { useTranslation, useHistory } from '@shared/core';
import { NATIONAL_COUNTRY, NATIONAL_COUNTRY_CODE } from '@apps/admin/common/ShippingAddress/constants';
import { yupSchema } from '@apps/guestList/utils';
import { ContactInputFields, Contacts } from '@apps/guestList/GuestList.types';

const contactInitialValue: ContactInputFields = {
  firstName: '',
  lastName: '',
  phoneNumber: '',
  email: '',
  address1: '',
  address2: '',
  postalCode: '',
  city: '',
  region: '',
  country: NATIONAL_COUNTRY,
  countryCode: NATIONAL_COUNTRY_CODE,
  validated: false
};

const initialValues: Contacts = {
  contacts: [contactInitialValue]
};

export const useCreateController = (eventId: string, eventHandle: string, firebaseEventId: string, contactCollectionId: string) => {
  const history = useHistory();
  const [e164PhoneNumber, setE164PhoneNumber] = useState<{ [key: number]: string }>({ 0: '' });
  const { t } = useTranslation('guestList');
  const tAdmin = t('admin');
  const { toast } = useToast();

  const [createContactsMutation, { loading: isCreateContactsMutationLoading }] = useCreateContactsWithoutTagsGuestListMutation({
    // we need to refetch because getEventContacts query returns a JSON and this creation won't update that response
    refetchQueries: [
      {
        query: GetEventContactsDocument,
        variables: {
          firebaseEventId,
          eventId,
          forceGuestListSynch: true
        }
      }
    ],
    awaitRefetchQueries: true
  });

  const formik = useFormik<Contacts>({
    initialValues,
    validationSchema: yupSchema(tAdmin.required(), tAdmin.invalidEmail()),
    validateOnMount: true,
    onSubmit: async (values, formikHelpers) => {
      try {
        const mutationData = values.contacts.map((contact, index) => ({
          address:
            // we do not want to save addresses from household members which are not the head of the household
            // also since address is optional we are not saving partial address, we save it only when it's complete
            index === 0 && contact.address1 && contact.city && contact.country && contact.countryCode && contact.postalCode && contact.region
              ? {
                  address1: contact.address1,
                  address2: contact.address2,
                  city: contact.city,
                  country: contact.country,
                  countryCode: contact.countryCode,
                  postalCode: contact.postalCode,
                  region: contact.region,
                  validated: contact.validated
                }
              : undefined,
          contact: {
            contactCollectionId,
            firebaseEventId,
            eventId,
            email: contact.email ? contact.email.trim() : undefined,
            firstName: contact.firstName.trim(),
            lastName: contact.lastName.trim(),
            party: values.contacts.length > 1 ? `${contactCollectionId}-${values.contacts[0].firstName}-${values.contacts[0].lastName}` : undefined
          },
          phoneNumbers: contact.phoneNumber
            ? [
                {
                  allowSMS: true,
                  mobile: true,
                  number: contact.phoneNumber.trim()
                }
              ]
            : undefined
        }));
        await createContactsMutation({ variables: { data: mutationData } });
        setE164PhoneNumber({ 0: '' });
        formikHelpers.resetForm();
        history.push(`/${eventHandle}/edit/guestlist`);
        toast(`${values.contacts.length > 1 ? tAdmin.guests() : tAdmin.guest()} ${tAdmin.created()} ${tAdmin.successfully()}.`, { position: 'bottom-center' });
      } catch {
        //add telemetry
        toast(`${tAdmin.error()} ${tAdmin.create()}.`, { position: 'bottom-center' });
      }
    }
  });

  return {
    formik,
    isMutationLoading: isCreateContactsMutationLoading,
    e164PhoneNumber,
    setE164PhoneNumber
  };
};
